import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import Tag from "go-app/components/Tags/Tag";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { StickyColumnWithEntityStatus } from "go-core/components/StickyColumnWithEntityStatus";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import RenderColumnUtils from "go-list/core/components/Actions/services/RenderColumnUtils";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig, ListParamsType, MultipleActionsParams } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { StockAdjustmentApi } from "../../../../../../../../../../services/Api/Organization/types";
import { StockAdjustmentSummaryApi } from "../../../../services/types";
import { stockAdjustmentListName, stockAdjustmentResourceType } from "../../../../utils";
import { getConfigFieldsWithSummaryData } from "../../../../utils/summaryDataUtils";
import StockAdjustmentStatusLabel, {
	getStockAdjustmentEntityStatus,
} from "../../components/StockAdjustmentStatusLabel";

interface ListState {
	resource?: Record<string, any>;
}

const buildConfig = (
	t: TFunction,
	organization: OrganizationState,
	onRestore: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	onCancelClose: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	onRemove: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	onConfirm: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	onCancel: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	onClose: (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => Promise<void>,
	data: Record<string, any>,
	location: Location,
	segmentListService: SegmentListService,
	summaryData: StockAdjustmentSummaryApi | undefined,
	setSummaryData: (summaryData: StockAdjustmentSummaryApi) => void
) => {
	let config = {
		fields: [
			{
				id: "date",
				name: t("modules.stock_adjustment.field.adjusted_at.title"),
				type: "date",
				render: (item: StockAdjustmentApi) => (
					<StickyColumnWithEntityStatus
						status={getStockAdjustmentEntityStatus(item.status)}
						to={`${location.pathname}/${item.id}`}
					>
						{FormatDate(item.adjusted_at)}
					</StickyColumnWithEntityStatus>
				),
				renderExport: (item: StockAdjustmentApi) => FormatDate(item.adjusted_at),
			},
			{
				id: "createdAt",
				name: t("common.word.created_at", { ns: "lib" }),
				type: "date",
				render: (item: StockAdjustmentApi) => FormatDate(item.created_at, undefined, true),
			},
			{
				id: "documentNumber",
				name: t("modules.stock_adjustment.field.document_number.title"),
				type: "text",
				render: (item: StockAdjustmentApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{item?.document_number}</Link>
				),
				renderExport: (item: StockAdjustmentApi) => item.document_number,
			},
			{
				id: "referenceNumber",
				name: t("common.word.reference_number"),
				type: "text",
				render: (item: StockAdjustmentApi) => item.reference_number,
			},
			{
				id: "comment",
				name: t("common.word.comment.title"),
				type: "text",
			},
			{
				id: "contact",
				name: t("common.word.contact"),
				render: (item: StockAdjustmentApi) => (
					<Link to={`/${organization.id}/contacts/${item?.contact_id}`} rel="noreferrer">
						{item?.contact?.name}
					</Link>
				),
				renderExport: (item: StockAdjustmentApi) => item?.contact?.name,
			},
			{
				id: "stock",
				name: t("common.word.stock"),
				render: (item: StockAdjustmentApi) => (
					<Link to={`/${organization.id}/stocks/${item?.stock_id}`} rel="noreferrer">
						{item?.stock?.name}
					</Link>
				),
				renderExport: (item: StockAdjustmentApi) => item?.stock?.name,
			},
			{
				id: "status",
				type: "list",
				listOptions: {
					OPEN: t("enums.common.status.OPEN"),
					DRAFT: t("enums.common.status.DRAFT"),
					CLOSED: t("enums.common.status.CLOSED"),
					VOID: t("enums.common.status.VOID"),
				},
				name: t("lib:common.word.status"),
				render: (item: StockAdjustmentApi) => <StockAdjustmentStatusLabel status={item.status} />,
				renderExport: (item: StockAdjustmentApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
						const tags = await apiOrganization.getTagSearchSelect(search, params, {
							cancelToken: options?.token,
						});

						return tags.map((tag) => ({
							id: tag.label,
							label: tag.label,
						}));
					},
				},
				render: (item: StockAdjustmentApi) => (
					<div className="d-flex flex-wrap">
						{item?.tags?.map((tag, i) => (
							<Tag className="mb-1" key={`tag-${i}`} title={tag.name} />
						))}
					</div>
				),
				renderExport: (item: StockAdjustmentApi) => item?.tags?.map((tag) => tag.name).join(", "),
			},
			{
				id: "quantity",
				name: t("common.word.quantity"),
			},
			{
				id: "totalPriceNetMoney",
				name: t("modules.stock_adjustment.field.total_price_net_money.title"),
				type: "number",
				render: (item: StockAdjustmentApi) => FormatMoney(item.total_price_net_money),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
			{
				id: "totalFifoCostNetMoney",
				name: t("modules.stock_adjustment.field.total_fifo_cost_net_money.title"),
				type: "number",
				render: (item: StockAdjustmentApi) => FormatMoney(item.total_fifo_cost_net_money),
				styleOverride: RenderColumnUtils.getMoneyStyles(),
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: StockAdjustmentApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: StockAdjustmentApi) => `${location.pathname}/${item.id}/edit`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: StockAdjustmentApi) => onRemove(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "DRAFT" || item.status === "VOID",
				refreshListAfter: true,
			},
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: (item: StockAdjustmentApi) => onConfirm(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "DRAFT",
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: (item: StockAdjustmentApi) => onCancel(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "OPEN",
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: (item: StockAdjustmentApi) => onClose(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "OPEN",
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel_close"),
				click: (item: StockAdjustmentApi) => onCancelClose(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "CLOSED",
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: (item: StockAdjustmentApi) => onRestore(item.id),
				visible: (item: StockAdjustmentApi) => item.status === "VOID",
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: (item: StockAdjustmentApi) => onRemove(item.id),
				visible: (item: StockAdjustmentApi) => item.status !== "DRAFT" && item.status !== "VOID",
				refreshListAfter: true,
			},
		],
		multipleActions: [
			{
				name: t("common.action.confirm", { ns: "lib" }),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onConfirm(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) => items.filter((item) => item.status === "DRAFT").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel", { ns: "lib" }),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onCancel(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) => items.filter((item) => item.status === "OPEN").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.close", { ns: "lib" }),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onClose(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) => items.filter((item) => item.status === "OPEN").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.cancel_close"),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onCancelClose(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) => items.filter((item) => item.status === "CLOSED").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onRemove(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) =>
					items.filter((item) => item.status === "DRAFT" || item.status === "VOID").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.restore", { ns: "lib" }),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onRestore(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) => items.filter((item) => item.status === "VOID").length > 0,
				refreshListAfter: true,
			},
			{
				name: t("common.action.void"),
				click: async (
					items: StockAdjustmentApi[],
					_: ListParamsType,
					areAllItemsSelected?: boolean,
					multipleActionsParams?: MultipleActionsParams
				) => {
					const ids = items.map((item) => item.id).join(",");
					await onRemove(ids, multipleActionsParams, areAllItemsSelected);
				},
				visible: (items: StockAdjustmentApi[]) =>
					items.filter((item) => item.status !== "DRAFT" && item.status !== "VOID").length > 0,
				refreshListAfter: true,
			},
		],
		selectedColumns: [
			"date",
			"documentNumber",
			"referenceNumber",
			"contact",
			"stock",
			"status",
			"quantity",
			"tagQuery",
			"totalPriceNetMoney",
			"totalFifoCostNetMoney",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		filters: [
			{
				id: "stockIds",
				type: "search_select",
				name: t("common.word.stock"),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getStockSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "contactIds",
				type: "search_select",
				name: t("common.word.contact"),
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getContactSearchSelect(search, params, {
							cancelToken: options?.token,
						}),
				},
			},
		],
		exportConfig: {
			title: t("modules.stock_adjustment.field.export_config.title"),
			filename: t("modules.stock_adjustment.header.title"),
			organization: organization.name,
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "custom_fields,contact,stock,tags";
			const response = await apiOrganization.getStockAdjustments(params, sourceToken?.token);

			setSummaryData(response.summary);
			return response;
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentListService.save(stockAdjustmentListName, stockAdjustmentResourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		fetchTotalItemsCountForSelectedFilters: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			const newParams = { ...params, include: "all" };
			return apiOrganization.getStockAdjustmentsCount(newParams, { cancelToken: sourceToken?.token });
		},
	} as ListConfig;

	const summaryDataConfig = {
		totalFifoCostNetMoney: FormatMoney(summaryData?.total_fifo_cost_net_money),
		totalPriceNetMoney: FormatMoney(summaryData?.total_price_net_money),
	};

	config.fields = getConfigFieldsWithSummaryData(t, summaryDataConfig, config.fields, summaryData);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};
const List = ({ resource }: ListState) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const [summaryData, setSummaryData] = useState<StockAdjustmentSummaryApi | undefined>(undefined);
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;

	const onRestore = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.restoreStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.restoreStockAdjustment(ids);
		}
		addSuccessFlash(t("common.flash.restored", { ns: "lib" }));
	};

	const onCancelClose = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.closeCancelStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.closeCancelStockAdjustment(ids);
		}
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onRemove = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.removeStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.removeStockAdjustment(ids);
		}
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const onConfirm = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.confirmStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.confirmStockAdjustment(ids);
		}
		addSuccessFlash(t("lib:common.flash.completed"));
	};

	const onCancel = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.cancelStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.cancelStockAdjustment(ids);
		}
		addSuccessFlash(t("common.flash.canceled", { ns: "lib" }));
	};

	const onClose = async (
		ids: string | number,
		multipleActionsParams?: MultipleActionsParams,
		areAllItemsSelected?: boolean
	) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.action", { ns: "lib" }),
		});

		const isMultipleAction = typeof ids === "string";
		if (isMultipleAction) {
			await apiOrganization.closeStockAdjustments(ids, multipleActionsParams, areAllItemsSelected);
		} else {
			await apiOrganization.closeStockAdjustment(ids);
		}
		addSuccessFlash(t("common.flash.closed", { ns: "lib" }));
	};
	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/documents/stock_adjustments/new`,
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `/${organization.id}/documents/stock_adjustments/new`,
		},
	];

	const data = resource.read();
	const config = buildConfig(
		t,
		organization,
		onRestore,
		onCancelClose,
		onRemove,
		onConfirm,
		onCancel,
		onClose,
		data,
		location,
		segmentContextValue,
		summaryData,
		setSummaryData
	);

	return (
		<>
			{!isMobile && <Header title={t("modules.stock_adjustment.header.title")} buttons={buttons} />}
			<ListData
				config={config}
				emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
				mobileActions={mobileActions}
				canManageMultiActionsForAllItems
				shouldChangeSegmentFromOutside
			/>
		</>
	);
};

const OrganizationStockAdjustmentIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const { t } = useTranslation();
	const customFieldResourceTypes = [
		{
			type: stockAdjustmentResourceType,
			name: t(`enums.custom_fields.resources.STOCK_ADJUSTMENT`),
		},
	];
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.stock_adjustment.header.title"));
		setResource(
			wrapPromise(
				segmentContextValue.get(stockAdjustmentListName, stockAdjustmentResourceType, customFieldResourceTypes)
			)
		);
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} />
		</Suspense>
	);
};
export default OrganizationStockAdjustmentIndexPage;
